import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getErrorMessage } from 'utils';

import { PagingRequestValues } from 'modules/organization/organizationApiTypes';
import {
  startDate,
  endDate,
  getBudgetDataSuccess,
  getGlobalFilterDimensions,
  invalidatePerformanceCache,
  advancedConfiguration,
} from 'modules/performance/performanceSlice';

import { xandrApi } from './xandrApi';
import {
  AudiencesPagingResponse,
  InsertionOrder,
  InsertionOrderAndLineItemEditRequest,
  InsertionOrderEditRequest,
  InsertionOrderEnableRequest,
  LineItem,
  LineItemEditRequest,
  LineItemsPagingResponse,
  XandrConnection,
  XandrConnectionRequest,
  XandrStatus,
} from './xandrApiTypes';
import {
  editInsertionOrderFailure,
  editInsertionOrderStart,
  editInsertionOrderSuccess,
  editLineItemFailure,
  editLineItemSuccess,
  editLineItemStart,
  enableInsertionOrderFailure,
  enableInsertionOrderStart,
  enableInsertionOrderSuccess,
  getInsertionOrdersFailure,
  getInsertionOrdersStart,
  getInsertionOrdersSuccess,
  getXandrConnectionFailure,
  getXandrConnectionStart,
  getXandrConnectionSuccess,
  getXandrStatusFailure,
  getXandrStatusStart,
  getXandrStatusSuccess,
  refreshXandrConnectionFailure,
  refreshXandrConnectionStart,
  refreshXandrConnectionSuccess,
  removeXandrConnectionFailure,
  removeXandrConnectionStart,
  removeXandrConnectionSuccess,
  syncInsertionOrderFailure,
  syncInsertionOrderStart,
  syncInsertionOrderSuccess,
  syncAllInsertionOrdersStart,
  syncAllInsertionOrdersSuccess,
  syncAllInsertionOrdersFailure,
  syncLineItemFailure,
  syncLineItemSuccess,
  syncLineItemStart,
  getLineItemsSuccess,
  getLineItemsFailure,
  getLineItemsStart,
  getAudiencesStart,
  getAudiencesSuccess,
  getAudiencesFailure,
  editInsertionOrdersAndLineItemsStart,
  editInsertionOrdersAndLineItemsSuccess,
  editInsertionOrdersAndLineItemsFailure,
} from './xandrSlice';
import { getCurrentAdvertiserId } from '../organization/organizationSlice';
import {
  AdvancedConfiguration,
  BudgetRequest,
  BudgetResponse,
  DimensionFilter,
} from '../performance/performanceApiTypes';
import { CAMPAIGN_DIMENSION } from '../performance/constants';
import { performanceApi } from '../performance/performanceApi';
import { dataApiSlice } from '../apiData/dataApiSlice';

function* getXandrConnectionFlow(action: PayloadAction<XandrConnectionRequest>) {
  try {
    const response: XandrConnection = yield call(
      xandrApi.getXandrConnection,
      action.payload.advertiserId,
      action.payload.xandrAdvertiserId,
    );

    yield put(getXandrConnectionSuccess(response));
  } catch (error) {
    yield put(getXandrConnectionFailure(getErrorMessage(error)));
  }
}

function* getXandrStatusFlow(action: PayloadAction<string>) {
  try {
    const response: XandrStatus = yield call(xandrApi.getXandrStatus, action.payload);

    yield put(getXandrStatusSuccess(response));
  } catch (error) {
    yield put(getXandrStatusFailure(getErrorMessage(error)));
  }
}

function* refreshXandrConnectionFlow(action: PayloadAction<string>) {
  try {
    const response: XandrConnection = yield call(xandrApi.refreshXandrConnection, action.payload);

    yield put(refreshXandrConnectionSuccess(response));
    yield put(invalidatePerformanceCache());
  } catch (error) {
    yield put(refreshXandrConnectionFailure(getErrorMessage(error)));
  }
}

function* removeXandrConnectionFlow(action: PayloadAction<string>) {
  try {
    const response: XandrConnection = yield call(xandrApi.removeXandrConnection, action.payload);

    yield put(removeXandrConnectionSuccess(response));
    yield put(invalidatePerformanceCache());
  } catch (error) {
    yield put(removeXandrConnectionFailure(getErrorMessage(error)));
  }
}

function* editInsertionOrdersAndLineItemsFlow(action: PayloadAction<InsertionOrderAndLineItemEditRequest>) {
  try {
    const { insertionOrders, lineItems } = action.payload;

    const effects = [];

    if (insertionOrders) effects.push(call(xandrApi.editInsertionOrders, insertionOrders));
    if (lineItems) effects.push(call(xandrApi.editLineItems, lineItems));
    yield all(effects);

    const response: InsertionOrder[] = yield call(
      xandrApi.getInsertionOrders,
      action.payload.advertiserId
    )

    yield put(editInsertionOrdersAndLineItemsSuccess(response));
  } catch (error) {
    yield put(editInsertionOrdersAndLineItemsFailure(getErrorMessage(error)));
  }
}

function* editLineItemFlow(
  action: PayloadAction<
    LineItemEditRequest & { resolve: () => void; reject: (error: string) => void }
  >,
) {
  try {
    const response: InsertionOrder[] = yield call(
      xandrApi.editLineItem,
      action.payload.lineItem,
      action.payload.advertiserId,
    );

    yield put(editLineItemSuccess(response));
    yield put(invalidatePerformanceCache());
    action.payload.resolve();
  } catch (error) {
    const message = getErrorMessage(error);

    yield put(editLineItemFailure(message));
    action.payload.reject(message);
  }
}

function* syncLineItemFlow(action: PayloadAction<number>) {
  try {
    const response: LineItem = yield call(xandrApi.syncLineItem, action.payload);

    yield put(syncLineItemSuccess(response));
    yield put(invalidatePerformanceCache());
  } catch (error) {
    yield put(syncLineItemFailure(getErrorMessage(error)));
  }
}

function* getInsertionOrdersFlow(action: PayloadAction<string>) {
  try {
    const response: InsertionOrder[] = yield call(xandrApi.getInsertionOrders, action.payload);

    yield put(getInsertionOrdersSuccess(response));
  } catch (error) {
    yield put(getInsertionOrdersFailure(getErrorMessage(error)));
  }
}

function* editInsertionOrderFlow(
  action: PayloadAction<
    InsertionOrderEditRequest & { resolve: () => void; reject: (error: string) => void }
  >,
) {
  try {
    const response: InsertionOrder[] = yield call(
      xandrApi.editInsertionOrder,
      action.payload.insertionOrder,
      action.payload.advertiserId,
    );

    yield put(editInsertionOrderSuccess(response));
    yield put(invalidatePerformanceCache());
    action.payload.resolve();
  } catch (error) {
    const message = getErrorMessage(error);

    yield put(editInsertionOrderFailure(message));
    action.payload.reject(message);
  }
}

function* syncInsertionOrderFlow(action: PayloadAction<number>) {
  try {
    const response: InsertionOrder = yield call(xandrApi.syncInsertionOrder, action.payload);

    yield put(syncInsertionOrderSuccess(response));
    yield put(invalidatePerformanceCache());
  } catch (error) {
    yield put(syncInsertionOrderFailure(getErrorMessage(error)));
  }
}

function* syncAllInsertionOrdersFlow(action: PayloadAction<string>) {
  try {
    const response: InsertionOrder[] = yield call(xandrApi.syncAllInsertionOrders, action.payload);

    yield put(syncAllInsertionOrdersSuccess(response));

    const [selectedAdvertiserId, globalFilterDimensions]: [string, DimensionFilter[]] = yield all([
      select(getCurrentAdvertiserId),
      select(getGlobalFilterDimensions),
    ]);

    // Refresh GlobalFilter values that can be updated in Xandr Connection
    yield all([
      put(dataApiSlice.util.invalidateTags([{ type: 'ApiData', id: 'InsertionOrders' }])),
      put(dataApiSlice.util.invalidateTags([{ type: 'ApiData', id: 'LineItems' }])),
      put(dataApiSlice.util.invalidateTags([{ type: 'ApiData', id: 'Audiences' }])),
    ]);

    const filteredDimensions = globalFilterDimensions.filter(d => d.dimension === CAMPAIGN_DIMENSION);
    const advancedConfigurations: AdvancedConfiguration = yield select(advancedConfiguration);
    const selectedStartDate: string = yield select(startDate);
    const selectedEndDate: string = yield select(endDate);

    if (filteredDimensions.length === 0) return;

    const budgetRequest: BudgetRequest = {
      advertiserId: selectedAdvertiserId,
      dimensionFilters: filteredDimensions,
      methodology: advancedConfigurations.methodology,
      conversionWindow: advancedConfigurations.conversionWindow,
      startDateTime: selectedStartDate,
      endDateTime: selectedEndDate,
    };

    // Fetch budget again in case it was updated too
    const budgetResponse: BudgetResponse = yield call(performanceApi.getBudgetData, budgetRequest);

    yield put(getBudgetDataSuccess(budgetResponse));
  } catch (error) {
    yield put(syncAllInsertionOrdersFailure(getErrorMessage(error)));
  }
}

function* enableInsertionOrderFlow(action: PayloadAction<InsertionOrderEnableRequest>) {
  try {
    const response: InsertionOrder[] = yield call(xandrApi.enableInsertionOrder, action.payload);

    yield put(enableInsertionOrderSuccess(response));
    yield put(invalidatePerformanceCache());
  } catch (error) {
    yield put(enableInsertionOrderFailure(getErrorMessage(error)));
  }
}

function* getLineItemsFlow(action: PayloadAction<PagingRequestValues>) {
  try {
    const response: LineItemsPagingResponse = yield call(xandrApi.getLineItems, action.payload);

    yield put(getLineItemsSuccess(response.data));
  } catch (error) {
    yield put(getLineItemsFailure(getErrorMessage(error)));
  }
}

function* getAudiencesFlow(action: PayloadAction<PagingRequestValues>) {
  try {
    const response: AudiencesPagingResponse = yield call(xandrApi.getAudiences, action.payload);

    yield put(getAudiencesSuccess(response.data));
  } catch (error) {
    yield put(getAudiencesFailure(getErrorMessage(error)));
  }
}

export function* xandrSagas() {
  yield all([
    takeLatest(getXandrConnectionStart.type, getXandrConnectionFlow),
    takeLatest(refreshXandrConnectionStart.type, refreshXandrConnectionFlow),
    takeLatest(removeXandrConnectionStart.type, removeXandrConnectionFlow),
    takeLatest(editInsertionOrdersAndLineItemsStart.type, editInsertionOrdersAndLineItemsFlow),
    takeLatest(getXandrStatusStart.type, getXandrStatusFlow),
    takeLatest(getInsertionOrdersStart.type, getInsertionOrdersFlow),
    takeLatest(editLineItemStart.type, editLineItemFlow),
    takeLatest(syncLineItemStart.type, syncLineItemFlow),
    takeLatest(editInsertionOrderStart.type, editInsertionOrderFlow),
    takeLatest(syncInsertionOrderStart.type, syncInsertionOrderFlow),
    takeLatest(syncAllInsertionOrdersStart.type, syncAllInsertionOrdersFlow),
    takeLatest(enableInsertionOrderStart.type, enableInsertionOrderFlow),
    takeLatest(getLineItemsStart.type, getLineItemsFlow),
    takeLatest(getAudiencesStart.type, getAudiencesFlow),
  ]);
}
